import { useState } from 'react';
import { Button, Field, Icon, Input, Panel } from '@dayon/design-system';
import { api, ApiRequestError } from '../../lib/api.ts';

/**
 * CHANGING YOUR OWN PASSWORD — the control the console did not have.
 *
 * ── THE TWO CONSEQUENCES, STATED BEFORE THE PRESS ─────────────────────────
 *
 * Every other session ends, and every browser told to remember this account goes
 * back to asking for a code. Both are surprising unless said, and the second one
 * is the one that will generate a support call: somebody's own laptop starts
 * asking for an authenticator code and they conclude the password change broke
 * something.
 *
 * Saying it is also the visible face of a security property. A trusted browser
 * SKIPS the second factor, so a password change that left one trusted would have
 * handed that browser the new password — which is why the server un-trusts them,
 * and why nobody should later "fix" the inconvenience without reading this.
 *
 * ── AND THE ONE THAT IS NOT A CONSEQUENCE ─────────────────────────────────
 *
 * This session SURVIVES. Said plainly, because the reset-link screen says the
 * opposite about itself and somebody who has used that will expect to be thrown
 * out here too.
 */
export interface PasswordPanelProps {
  /** From the contract, so the screen cannot disagree with the server's floor. */
  minimum: number;
  updatedAt: string | null;
  onChanged: (result: { otherSessionsEnded: number; trustedDevicesEnded: number }) => void;
  onFailure: (title: string, error: unknown) => void;
}

export function PasswordPanel({ minimum, updatedAt, onChanged, onFailure }: PasswordPanelProps) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  /* Field-level, from the server's `fields` map — so the wrong box is marked
     rather than a banner appearing that leaves the person guessing which. */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const tooShort = next.length > 0 && next.length < minimum;
  const ready = current.length > 0 && next.length >= minimum && !busy;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setFieldErrors({});
    try {
      const answer = await api.post<{
        otherSessionsEnded: number;
        trustedDevicesEnded: number;
      }>('/api/auth/account/password', { currentPassword: current, newPassword: next });
      setCurrent('');
      setNext('');
      onChanged(answer);
    } catch (error) {
      if (error instanceof ApiRequestError && error.fields !== undefined) {
        setFieldErrors(error.fields);
      } else {
        /* Everything that is NOT about a specific box goes to the modal — a
           lockout, a dropped connection, a 500. */
        onFailure('Could not change your password', error);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Password">
      <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
        <p className="text-subtle">
          {updatedAt === null
            ? 'You have never changed it. It is still the one set when your account was created.'
            : `Last changed ${new Date(updatedAt).toLocaleDateString('en-PH', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}.`}
        </p>
        <Field
          label="Current password"
          {...(fieldErrors['currentPassword'] !== undefined
            ? { error: fieldErrors['currentPassword'] }
            : {})}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          )}
        </Field>

        <Field
          label="New password"
          hint={`At least ${minimum} characters.`}
          {...(tooShort
            ? { error: `At least ${minimum} characters — this account can move money.` }
            : fieldErrors['newPassword'] !== undefined
              ? { error: fieldErrors['newPassword'] }
              : {})}
        >
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid}
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          )}
        </Field>

        <div className="notice notice-warn">
          <Icon name="warning" />
          <div>
            <p>
              <strong>Every other session ends.</strong> Anywhere else this account is signed in
              stops working at the next click.
            </p>
            <p>
              <strong>Saved devices will ask for a code again.</strong> Any browser where you
              ticked &ldquo;Trust this device&rdquo; goes back to asking for your authenticator.
              That is deliberate: a trusted browser skips the code, so leaving one trusted would
              leave it needing nothing but your new password.
            </p>
            <p>
              <strong>You stay signed in here.</strong> Only this tab survives.
            </p>
          </div>
        </div>

        <div>
          <Button variant="primary" type="submit" busy={busy} disabled={!ready}>
            Change password
          </Button>
        </div>
      </form>
    </Panel>
  );
}
